import React from "react";
import styled from "styled-components";
import { Text, COLOR_SYSTEM, Tag } from "components/atoms";
import { PointsTransaction, PointsTransactionType, OrderInfo } from "common/models/types";

/**
 * 포인트 거래내역 리스트 — 충전/사용/환불/지급 등 포인트 원장 전체를 시간순으로 표시.
 * 기존 PointsPage(/mypage/points)의 거래내역 렌더 로직을 추출해 재사용.
 */

const getTransactionTypeText = (type: PointsTransactionType): string => {
  switch (type) {
    case PointsTransactionType.PURCHASE:
      return "포인트 구매";
    case PointsTransactionType.ADMIN_GRANT:
      return "관리자 지급";
    case PointsTransactionType.AI_ANALYSIS:
      return "AI 분석 사용";
    case PointsTransactionType.REFUND:
      return "환불";
    case PointsTransactionType.BONUS:
      return "보너스";
    case PointsTransactionType.EVENT:
      return "이벤트";
    default:
      return "기타";
  }
};

const getTransactionTypeTag = (
  type: PointsTransactionType
): { label: string; color: string } => {
  switch (type) {
    case PointsTransactionType.PURCHASE:
      return { label: "구매", color: "Blue" };
    case PointsTransactionType.ADMIN_GRANT:
      return { label: "지급", color: "Green" };
    case PointsTransactionType.AI_ANALYSIS:
      return { label: "사용", color: "Red" };
    case PointsTransactionType.REFUND:
      return { label: "환불", color: "Yellow" };
    case PointsTransactionType.BONUS:
      return { label: "보너스", color: "Purple" };
    case PointsTransactionType.EVENT:
      return { label: "이벤트", color: "Teal" };
    default:
      return { label: "기타", color: "Gray" };
  }
};

const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface Props {
  transactions: PointsTransaction[];
  /** 충전(PURCHASE) 항목의 영수증 링크 매핑용 주문 목록. 영수증 URL은 주문에만 있음. */
  orders?: OrderInfo[];
  emptyText?: string;
}

const PointsTransactionList: React.FC<Props> = ({
  transactions,
  orders = [],
  emptyText = "포인트 거래내역이 없습니다.",
}) => {
  if (!transactions.length) {
    return <Empty>{emptyText}</Empty>;
  }
  // 주문ID·가맹점주문ID 어느 쪽으로 참조되더라도 매칭되도록 두 키 모두 등록. 매치 없으면 미표시(안전).
  const receiptByRef = new Map<string, string>();
  orders.forEach((o) => {
    if (!o.payReceiptUrl) return;
    if (o.id != null) receiptByRef.set(String(o.id), o.payReceiptUrl);
    if (o.payMerchantId) receiptByRef.set(o.payMerchantId, o.payReceiptUrl);
  });
  return (
    <>
      {transactions.map((t) => {
        const tag = getTransactionTypeTag(t.transactionType);
        const receiptUrl =
          t.transactionType === PointsTransactionType.PURCHASE && t.referenceId
            ? receiptByRef.get(t.referenceId)
            : undefined;
        return (
          <Item key={t.id}>
            <Info>
              <Header>
                <Tag size="Small" variant="Tinted" color={tag.color as any}>
                  {tag.label}
                </Tag>
                <Desc>
                  {t.description || getTransactionTypeText(t.transactionType)}
                </Desc>
              </Header>
              <DateText>{formatDate(t.createdAt || new Date())}</DateText>
              {receiptUrl && (
                <ReceiptLink
                  onClick={() => window.open(receiptUrl, "_blank", "width=600,height=800")}
                >
                  영수증 보기
                </ReceiptLink>
              )}
            </Info>
            <Amount isPositive={t.points > 0}>
              {t.points > 0 ? "+" : ""}
              {t.points.toLocaleString()}P
            </Amount>
          </Item>
        );
      })}
    </>
  );
};

export default PointsTransactionList;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Desc = styled(Text)`
  font-size: 15px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const DateText = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const ReceiptLink = styled.span`
  width: fit-content;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Blue")?.[500]};
  text-decoration: underline;
  cursor: pointer;
`;

const Amount = styled.div<{ isPositive: boolean }>`
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  color: ${({ isPositive }) =>
    isPositive ? COLOR_SYSTEM.get("Green")?.[600] : COLOR_SYSTEM.get("Red")?.[600]};
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-size: 14px;
`;
